import React, { useEffect, useMemo, useState } from 'react'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { Flex, Tabs as PillTab, TabsContainer } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { Input } from 'components/Input/Input'
import { CheckChip } from 'components/CheckBox/CheckChip'
import { ChipsContainer } from 'components/CheckBox/CheckBox.styled'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { IConfigurationTabProps } from 'types/foodLogistics/foodTabProps.interface'
import { useHasPermission } from 'hooks/useHasPermission'
import {
  archiveFoodDietaryTag,
  archiveFoodKitchen,
  createFoodCatchmentRule,
  createFoodDietaryTag,
  createFoodKitchen,
  createFoodMealWindow,
  deleteFoodCatchmentRule,
  deleteFoodMealWindow,
  getFoodCatchmentRules,
  getFoodConfigVersions,
  getFoodDietaryTags,
  getFoodKitchens,
  getFoodMealWindows,
  updateFoodCatchmentRule,
  updateFoodDietaryTag,
  updateFoodKitchen,
  updateFoodMealWindow,
  upsertFoodSitePolicy,
} from 'features/food/foodSlice'
import { FoodConfigUnknownPresencePolicy, FoodProvisioningMode } from 'types/api/foodApi.interface'
import { formatDateForDisplay } from 'utils/helpers'
import {
  FOOD_PROVISIONING_MODE_OPTIONS,
  FOOD_UNKNOWN_PRESENCE_OPTIONS,
  foodProvisioningModeLabel,
} from '../foodPlanningData'

const CONFIG_SUB_TABS = [
  'Dietary Tags',
  'Kitchens',
  'Meal Windows',
  'Catchment Rules',
  'Site Policy',
  'Config Versions',
]

const operatorOptions = [
  { label: 'Internal', value: 1 },
  { label: 'Contractor', value: 2 },
]

export const ConfigurationTab = ({ onToolbarChange }: IConfigurationTabProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const canKitchen = useHasPermission('kitchen')
  const { dietaryTags, logisticsKitchens, mealWindows, catchmentRules, configVersions, isLoading } =
    useSelector((store: RootState) => store.food)

  const visibleTabs = useMemo(
    () => CONFIG_SUB_TABS.filter((t) => t !== 'Kitchens' || canKitchen),
    [canKitchen],
  )

  const [tab, setTab] = useState<string>(CONFIG_SUB_TABS[0])

  useEffect(() => {
    if (!visibleTabs.includes(tab)) {
      setTab(visibleTabs[0] || CONFIG_SUB_TABS[0])
    }
  }, [visibleTabs, tab])

  const [dietaryCode, setDietaryCode] = useState('')
  const [dietaryName, setDietaryName] = useState('')
  const [dietaryEditCode, setDietaryEditCode] = useState<string | null>(null)

  const [kitchenId, setKitchenId] = useState<number | null>(null)
  const [kitchenName, setKitchenName] = useState('')
  const [kitchenOperatorType, setKitchenOperatorType] = useState(1)
  const [kitchenPackoutSupport, setKitchenPackoutSupport] = useState(true)
  const [kitchenStandbyEnabled, setKitchenStandbyEnabled] = useState(true)
  const [kitchenSoftCapacity, setKitchenSoftCapacity] = useState('0')
  const [kitchenTagCodes, setKitchenTagCodes] = useState<string[]>([])

  const [mealWindowId, setMealWindowId] = useState<number | null>(null)
  const [mealWindowName, setMealWindowName] = useState('')
  const [mealWindowStart, setMealWindowStart] = useState('06:00')
  const [mealWindowEnd, setMealWindowEnd] = useState('08:00')
  const [mealWindowCutoffTime, setMealWindowCutoffTime] = useState('20:00')
  const [mealWindowOffset, setMealWindowOffset] = useState('-1')
  const [mealWindowKitchenId, setMealWindowKitchenId] = useState<number | null>(null)

  const [catchmentId, setCatchmentId] = useState<number | null>(null)
  const [catchmentKitchenId, setCatchmentKitchenId] = useState<number | null>(null)
  const [catchmentLocationNodeId, setCatchmentLocationNodeId] = useState('')
  const [catchmentDepartmentId, setCatchmentDepartmentId] = useState('')
  const [catchmentFallback, setCatchmentFallback] = useState(false)

  const [maxMealsPerDay, setMaxMealsPerDay] = useState('3')
  const [priorityWindowIds, setPriorityWindowIds] = useState<number[]>([])
  const [unknownPresencePolicy, setUnknownPresencePolicy] = useState(
    FoodConfigUnknownPresencePolicy.NoMeal,
  )
  const [defaultMode, setDefaultMode] = useState(FoodProvisioningMode.MessOrHall)
  const [noShowGraceMinutes, setNoShowGraceMinutes] = useState('30')
  const [cancelIntentOnNoShow, setCancelIntentOnNoShow] = useState(true)
  const [applyShowRateCoefficient, setApplyShowRateCoefficient] = useState(false)

  useEffect(() => {
    dispatch(getFoodDietaryTags({ page: 1, limit: 200 }))
    dispatch(getFoodMealWindows({ page: 1, limit: 200 }))
    dispatch(getFoodCatchmentRules({ page: 1, limit: 200 }))
    dispatch(getFoodConfigVersions({ page: 1, limit: 50 }))
    if (canKitchen) {
      dispatch(getFoodKitchens({ page: 1, limit: 200 }))
    }
  }, [dispatch, canKitchen])

  useEffect(() => {
    if (!(mealWindows.data || []).length) return
    if (priorityWindowIds.length) return
    setPriorityWindowIds((mealWindows.data || []).map((w) => w.id))
  }, [mealWindows.data, priorityWindowIds.length])

  useEffect(() => {
    onToolbarChange?.(
      <TabsContainer>
        {visibleTabs.map((t) => (
          <PillTab key={t} active={tab === t} onClick={() => setTab(t)}>
            {t}
          </PillTab>
        ))}
      </TabsContainer>,
    )
    return () => onToolbarChange?.(null)
  }, [tab, onToolbarChange, visibleTabs])

  const resetDietary = () => {
    setDietaryCode('')
    setDietaryName('')
    setDietaryEditCode(null)
  }

  const resetKitchen = () => {
    setKitchenId(null)
    setKitchenName('')
    setKitchenOperatorType(1)
    setKitchenPackoutSupport(true)
    setKitchenStandbyEnabled(true)
    setKitchenSoftCapacity('0')
    setKitchenTagCodes([])
  }

  const resetMealWindow = () => {
    setMealWindowId(null)
    setMealWindowName('')
    setMealWindowStart('06:00')
    setMealWindowEnd('08:00')
    setMealWindowCutoffTime('20:00')
    setMealWindowOffset('-1')
    setMealWindowKitchenId(null)
  }

  const resetCatchment = () => {
    setCatchmentId(null)
    setCatchmentKitchenId(null)
    setCatchmentLocationNodeId('')
    setCatchmentDepartmentId('')
    setCatchmentFallback(false)
  }

  const submitDietary = async () => {
    if (!dietaryName.trim()) return
    if (dietaryEditCode) {
      await dispatch(updateFoodDietaryTag({ code: dietaryEditCode, body: { name: dietaryName.trim() } }))
    } else {
      if (!dietaryCode.trim()) return
      await dispatch(createFoodDietaryTag({ code: dietaryCode.trim(), name: dietaryName.trim() }))
    }
    resetDietary()
    dispatch(getFoodDietaryTags({ page: 1, limit: 200 }))
  }

  const submitKitchen = async () => {
    if (!kitchenName.trim()) return
    const body = {
      name: kitchenName.trim(),
      operatorType: Number(kitchenOperatorType),
      packoutSupport: kitchenPackoutSupport,
      softCapacity: Number(kitchenSoftCapacity),
      standbyEnabled: kitchenStandbyEnabled,
      dietaryTagCodes: kitchenTagCodes,
    }
    if (kitchenId) {
      await dispatch(updateFoodKitchen({ id: kitchenId, body }))
    } else {
      await dispatch(createFoodKitchen(body))
    }
    resetKitchen()
    dispatch(getFoodKitchens({ page: 1, limit: 200 }))
  }

  const submitMealWindow = async () => {
    if (!mealWindowName.trim()) return
    const body = {
      name: mealWindowName.trim(),
      serviceStart: mealWindowStart,
      serviceEnd: mealWindowEnd,
      cutoffTime: mealWindowCutoffTime,
      cutoffDayOffset: Number(mealWindowOffset),
      defaultKitchenId: mealWindowKitchenId,
    }
    if (mealWindowId) {
      await dispatch(updateFoodMealWindow({ id: mealWindowId, body }))
    } else {
      await dispatch(createFoodMealWindow(body))
    }
    resetMealWindow()
    dispatch(getFoodMealWindows({ page: 1, limit: 200 }))
  }

  const submitCatchment = async () => {
    if (!catchmentKitchenId) return
    const body = {
      kitchenId: catchmentKitchenId,
      locationNodeId: catchmentLocationNodeId.trim() ? Number(catchmentLocationNodeId) : null,
      departmentId: catchmentDepartmentId.trim() ? Number(catchmentDepartmentId) : null,
      isFallback: catchmentFallback,
    }
    if (catchmentId) {
      await dispatch(updateFoodCatchmentRule({ id: catchmentId, body }))
    } else {
      await dispatch(createFoodCatchmentRule(body))
    }
    resetCatchment()
    dispatch(getFoodCatchmentRules({ page: 1, limit: 200 }))
  }

  const dietaryColumns: IHonestTableColumn<any>[] = [
    { id: 'code', header: 'Code', cell: (row) => row.code },
    { id: 'name', header: 'Name', cell: (row) => row.name },
    { id: 'createdAt', header: 'Created', cell: (row) => row.createdAt?.slice(0, 10) || '—' },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <Flex gap='0.5rem'>
          <Button
            size='sm'
            variant='secondary'
            onClick={() => {
              setDietaryEditCode(row.code)
              setDietaryCode(row.code)
              setDietaryName(row.name)
            }}
          >
            Edit
          </Button>
          <Button size='sm' variant='secondary' onClick={() => dispatch(archiveFoodDietaryTag(row.code))}>
            Archive
          </Button>
        </Flex>
      ),
    },
  ]

  const kitchenColumns: IHonestTableColumn<any>[] = [
    { id: 'name', header: 'Kitchen', cell: (row) => row.name },
    { id: 'operatorType', header: 'Operator', cell: (row) => (row.operatorType === 2 ? 'Contractor' : 'Internal') },
    { id: 'softCapacity', header: 'Soft cap', cell: (row) => row.softCapacity },
    {
      id: 'dietaryTagCapabilities',
      header: 'Tags',
      cell: (row) => (
        <Flex gap='0.25rem' wrap>
          {(row.dietaryTagCapabilities || []).map((tag: any) => (
            <StatusPill key={tag.code} status='accent'>{tag.code}</StatusPill>
          ))}
        </Flex>
      ),
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <Flex gap='0.5rem'>
          <Button
            size='sm'
            variant='secondary'
            onClick={() => {
              setKitchenId(row.id)
              setKitchenName(row.name)
              setKitchenOperatorType(row.operatorType)
              setKitchenPackoutSupport(Boolean(row.packoutSupport))
              setKitchenStandbyEnabled(Boolean(row.standbyEnabled))
              setKitchenSoftCapacity(String(row.softCapacity))
              setKitchenTagCodes((row.dietaryTagCapabilities || []).map((tag: any) => tag.code))
            }}
          >
            Edit
          </Button>
          <Button size='sm' variant='secondary' onClick={() => dispatch(archiveFoodKitchen(row.id))}>
            Archive
          </Button>
        </Flex>
      ),
    },
  ]

  const mealWindowColumns: IHonestTableColumn<any>[] = [
    { id: 'name', header: 'Name', cell: (row) => row.name },
    { id: 'serviceStart', header: 'Start', cell: (row) => row.serviceStart },
    { id: 'serviceEnd', header: 'End', cell: (row) => row.serviceEnd },
    { id: 'cutoffTime', header: 'Cutoff', cell: (row) => `${row.cutoffTime} (${row.cutoffDayOffset})` },
    { id: 'defaultKitchenId', header: 'Default Kitchen', cell: (row) => row.defaultKitchenId || '—' },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <Flex gap='0.5rem'>
          <Button
            size='sm'
            variant='secondary'
            onClick={() => {
              setMealWindowId(row.id)
              setMealWindowName(row.name)
              setMealWindowStart(row.serviceStart)
              setMealWindowEnd(row.serviceEnd)
              setMealWindowCutoffTime(row.cutoffTime)
              setMealWindowOffset(String(row.cutoffDayOffset))
              setMealWindowKitchenId(row.defaultKitchenId)
            }}
          >
            Edit
          </Button>
          <Button size='sm' variant='secondary' onClick={() => dispatch(deleteFoodMealWindow(row.id))}>
            Delete
          </Button>
        </Flex>
      ),
    },
  ]

  const catchmentColumns: IHonestTableColumn<any>[] = [
    { id: 'kitchenId', header: 'Kitchen ID', cell: (row) => row.kitchenId },
    { id: 'locationNodeId', header: 'Location Node', cell: (row) => row.locationNodeId || '—' },
    { id: 'departmentId', header: 'Department', cell: (row) => row.departmentId || '—' },
    {
      id: 'isFallback',
      header: 'Fallback',
      cell: (row) => <StatusPill status={row.isFallback ? 'info' : 'neutral'}>{row.isFallback ? 'Yes' : 'No'}</StatusPill>,
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <Flex gap='0.5rem'>
          <Button
            size='sm'
            variant='secondary'
            onClick={() => {
              setCatchmentId(row.id)
              setCatchmentKitchenId(row.kitchenId)
              setCatchmentLocationNodeId(row.locationNodeId ? String(row.locationNodeId) : '')
              setCatchmentDepartmentId(row.departmentId ? String(row.departmentId) : '')
              setCatchmentFallback(Boolean(row.isFallback))
            }}
          >
            Edit
          </Button>
          <Button size='sm' variant='secondary' onClick={() => dispatch(deleteFoodCatchmentRule(row.id))}>
            Delete
          </Button>
        </Flex>
      ),
    },
  ]

  const togglePriorityWindow = (id: number) => {
    setPriorityWindowIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )
  }

  const movePriorityWindow = (id: number, dir: -1 | 1) => {
    setPriorityWindowIds((prev) => {
      const idx = prev.indexOf(id)
      if (idx < 0) return prev
      const next = [...prev]
      const swap = idx + dir
      if (swap < 0 || swap >= next.length) return prev
      const tmp = next[idx]
      next[idx] = next[swap]
      next[swap] = tmp
      return next
    })
  }

  const submitSitePolicy = async () => {
    const max = Number(maxMealsPerDay)
    if (!max || max < 1 || !priorityWindowIds.length) return
    await dispatch(
      upsertFoodSitePolicy({
        maxMealsPerDay: max,
        mealSelectionPriorityWindowIds: priorityWindowIds,
        unknownPresencePolicy,
        defaultMode,
        attendanceReconciliation: {
          noShowGraceMinutes: Number(noShowGraceMinutes) || 0,
          cancelIntentOnNoShow,
          applyShowRateCoefficient,
        },
      }),
    )
    dispatch(getFoodConfigVersions({ page: 1, limit: 50 }))
  }

  const versionColumns: IHonestTableColumn<any>[] = [
    { id: 'id', header: 'Version', cell: (row) => `#${row.id}` },
    {
      id: 'effectiveFromOperationalDay',
      header: 'Effective from',
      cell: (row) => formatDateForDisplay(row.effectiveFromOperationalDay),
    },
    {
      id: 'createdAt',
      header: 'Created',
      cell: (row) => formatDateForDisplay(row.createdAt, true),
    },
    { id: 'createdBy', header: 'Created by', cell: (row) => row.createdBy },
    {
      id: 'policy',
      header: 'Policy snapshot',
      minWidth: '16rem',
      cell: (row) => {
        const p = row.payload?.policy
        if (!p) return '—'
        return `${p.maxMealsPerDay} meals/day · ${foodProvisioningModeLabel(p.defaultMode)} · unknown=${p.unknownPresencePolicy}`
      },
    },
  ]

  return (
    <Flex column gap='1rem' width='100%'>
      {tab === 'Dietary Tags' && (
        <Container>
          <ContainerHeader><TitleText weight='700'>Dietary Tags</TitleText></ContainerHeader>
          <ContainerContent gap='1rem'>
            <Flex gap='1rem' wrap>
              <Input labelText='Code' value={dietaryCode} onChange={(e: any) => setDietaryCode(e.target.value)} disabled={Boolean(dietaryEditCode)} />
              <Input labelText='Name' value={dietaryName} onChange={(e: any) => setDietaryName(e.target.value)} />
              <Flex gap='0.5rem' alignEnd>
                <Button variant='primary' onClick={submitDietary}>{dietaryEditCode ? 'Update' : 'Create'}</Button>
                {dietaryEditCode && <Button variant='secondary' onClick={resetDietary}>Cancel</Button>}
              </Flex>
            </Flex>
            <HonestTable columns={dietaryColumns} data={dietaryTags.data || []} keyField='code' loading={isLoading} />
          </ContainerContent>
        </Container>
      )}

      {tab === 'Kitchens' && canKitchen && (
        <Container>
          <ContainerHeader><TitleText weight='700'>Kitchens</TitleText></ContainerHeader>
          <ContainerContent gap='1rem'>
            <Flex gap='1rem' wrap>
              <Input labelText='Name' value={kitchenName} onChange={(e: any) => setKitchenName(e.target.value)} />
              <Input labelText='Soft Capacity' type='number' value={kitchenSoftCapacity} onChange={(e: any) => setKitchenSoftCapacity(e.target.value)} />
              <HonestSelectInput
                labelText='Operator Type'
                options={operatorOptions}
                defaultValue={kitchenOperatorType}
                onChange={(v: any) => setKitchenOperatorType(Number(v.value))}
                name='operatorType'
              />
            </Flex>
            <Flex gap='0.5rem' wrap>
              {(dietaryTags.data || []).map((tag: any) => (
                <CheckChip
                  key={tag.code}
                  text={tag.code}
                  active={kitchenTagCodes.includes(tag.code)}
                  onClick={() =>
                    setKitchenTagCodes((prev) =>
                      prev.includes(tag.code) ? prev.filter((x) => x !== tag.code) : [...prev, tag.code],
                    )
                  }
                />
              ))}
            </Flex>
            <Flex gap='0.5rem' alignEnd>
              <Button variant={kitchenPackoutSupport ? 'primary' : 'secondary'} onClick={() => setKitchenPackoutSupport((v) => !v)}>
                Packout: {kitchenPackoutSupport ? 'Yes' : 'No'}
              </Button>
              <Button variant={kitchenStandbyEnabled ? 'primary' : 'secondary'} onClick={() => setKitchenStandbyEnabled((v) => !v)}>
                Standby: {kitchenStandbyEnabled ? 'Yes' : 'No'}
              </Button>
              <Button variant='primary' onClick={submitKitchen}>{kitchenId ? 'Update' : 'Create'}</Button>
              {kitchenId && <Button variant='secondary' onClick={resetKitchen}>Cancel</Button>}
            </Flex>
            <HonestTable columns={kitchenColumns} data={logisticsKitchens.data || []} keyField='id' loading={isLoading} />
          </ContainerContent>
        </Container>
      )}

      {tab === 'Meal Windows' && (
        <Container>
          <ContainerHeader><TitleText weight='700'>Meal Window Definitions</TitleText></ContainerHeader>
          <ContainerContent gap='1rem'>
            <Flex gap='1rem' wrap>
              <Input labelText='Name' value={mealWindowName} onChange={(e: any) => setMealWindowName(e.target.value)} />
              <Input labelText='Service Start' value={mealWindowStart} onChange={(e: any) => setMealWindowStart(e.target.value)} />
              <Input labelText='Service End' value={mealWindowEnd} onChange={(e: any) => setMealWindowEnd(e.target.value)} />
              <Input labelText='Cutoff Time' value={mealWindowCutoffTime} onChange={(e: any) => setMealWindowCutoffTime(e.target.value)} />
              <Input labelText='Cutoff Day Offset' type='number' value={mealWindowOffset} onChange={(e: any) => setMealWindowOffset(e.target.value)} />
              <Input labelText='Default Kitchen ID' type='number' value={mealWindowKitchenId ?? ''} onChange={(e: any) => setMealWindowKitchenId(e.target.value ? Number(e.target.value) : null)} />
            </Flex>
            <Flex gap='0.5rem'>
              <Button variant='primary' onClick={submitMealWindow}>{mealWindowId ? 'Update' : 'Create'}</Button>
              {mealWindowId && <Button variant='secondary' onClick={resetMealWindow}>Cancel</Button>}
            </Flex>
            <HonestTable columns={mealWindowColumns} data={mealWindows.data || []} keyField='id' loading={isLoading} />
          </ContainerContent>
        </Container>
      )}

      {tab === 'Catchment Rules' && (
        <Container>
          <ContainerHeader><TitleText weight='700'>Catchment Rules</TitleText></ContainerHeader>
          <ContainerContent gap='1rem'>
            <Flex gap='1rem' wrap>
              <Input labelText='Kitchen ID' type='number' value={catchmentKitchenId ?? ''} onChange={(e: any) => setCatchmentKitchenId(e.target.value ? Number(e.target.value) : null)} />
              <Input labelText='Location Node ID' type='number' value={catchmentLocationNodeId} onChange={(e: any) => setCatchmentLocationNodeId(e.target.value)} />
              <Input labelText='Department ID' type='number' value={catchmentDepartmentId} onChange={(e: any) => setCatchmentDepartmentId(e.target.value)} />
              <Button variant={catchmentFallback ? 'primary' : 'secondary'} onClick={() => setCatchmentFallback((v) => !v)}>
                Fallback: {catchmentFallback ? 'Yes' : 'No'}
              </Button>
            </Flex>
            <OtherText grey>When department is set, backend forces fallback to true.</OtherText>
            <Flex gap='0.5rem'>
              <Button variant='primary' onClick={submitCatchment}>{catchmentId ? 'Update' : 'Create'}</Button>
              {catchmentId && <Button variant='secondary' onClick={resetCatchment}>Cancel</Button>}
            </Flex>
            <HonestTable columns={catchmentColumns} data={catchmentRules.data || []} keyField='id' loading={isLoading} />
          </ContainerContent>
        </Container>
      )}

      {tab === 'Site Policy' && (
        <Container>
          <ContainerHeader>
            <TitleText weight='700'>Site Policy</TitleText>
          </ContainerHeader>
          <ContainerContent gap='1rem'>
            <OtherText grey>
              Saves stamp a new configuration version effective the next operational day.
            </OtherText>
            <Flex gap='1rem' wrap>
              <Input
                labelText='Max meals per day'
                type='number'
                value={maxMealsPerDay}
                onChange={(e: any) => setMaxMealsPerDay(e.target.value)}
              />
              <HonestSelectInput
                nomarg
                labelText='Unknown presence policy'
                options={FOOD_UNKNOWN_PRESENCE_OPTIONS}
                defaultValue={unknownPresencePolicy}
                onChange={(v: any) => setUnknownPresencePolicy(Number(v.value))}
                name='unknownPresence'
              />
              <HonestSelectInput
                nomarg
                labelText='Default provisioning mode'
                options={FOOD_PROVISIONING_MODE_OPTIONS}
                defaultValue={defaultMode}
                onChange={(v: any) => setDefaultMode(Number(v.value))}
                name='defaultMode'
              />
              <Input
                labelText='No-show grace (minutes)'
                type='number'
                value={noShowGraceMinutes}
                onChange={(e: any) => setNoShowGraceMinutes(e.target.value)}
              />
            </Flex>
            <Flex gap='0.5rem' wrap>
              <Button
                variant={cancelIntentOnNoShow ? 'primary' : 'secondary'}
                onClick={() => setCancelIntentOnNoShow((v) => !v)}
              >
                Cancel intent on no-show: {cancelIntentOnNoShow ? 'Yes' : 'No'}
              </Button>
              <Button
                variant={applyShowRateCoefficient ? 'primary' : 'secondary'}
                onClick={() => setApplyShowRateCoefficient((v) => !v)}
              >
                Apply show-rate coefficient: {applyShowRateCoefficient ? 'Yes' : 'No'}
              </Button>
            </Flex>
            <OtherText grey>MEAL SELECTION PRIORITY (order matters)</OtherText>
            <Flex column gap='0.5rem'>
              {(mealWindows.data || []).map((w) => {
                const active = priorityWindowIds.includes(w.id)
                const order = priorityWindowIds.indexOf(w.id)
                return (
                  <Flex key={w.id} between center gap='0.5rem' width='100%'>
                    <Button
                      variant={active ? 'primary' : 'secondary'}
                      size='sm'
                      onClick={() => togglePriorityWindow(w.id)}
                    >
                      {active ? `#${order + 1} ${w.name}` : w.name}
                    </Button>
                    {active && (
                      <Flex gap='0.25rem'>
                        <Button size='sm' variant='secondary' onClick={() => movePriorityWindow(w.id, -1)}>
                          Up
                        </Button>
                        <Button size='sm' variant='secondary' onClick={() => movePriorityWindow(w.id, 1)}>
                          Down
                        </Button>
                      </Flex>
                    )}
                  </Flex>
                )
              })}
            </Flex>
            <Button variant='primary' onClick={submitSitePolicy}>
              Save site policy
            </Button>
          </ContainerContent>
        </Container>
      )}

      {tab === 'Config Versions' && (
        <Container>
          <ContainerHeader>
            <TitleText weight='700'>Configuration Versions</TitleText>
          </ContainerHeader>
          <ContainerContent gap='1rem'>
            <OtherText grey>
              Immutable snapshots stamped automatically when kitchens, meal windows, dietary tags,
              catchment rules or site policy change. Newest first.
            </OtherText>
            <HonestTable
              columns={versionColumns}
              data={configVersions.data || []}
              keyField='id'
              loading={isLoading}
            />
          </ContainerContent>
        </Container>
      )}
    </Flex>
  )
}
